'use client';
/**
 * APERÇU DU CARTOUCHE — ce que le paramétrage « Identité & cartouche » fabrique.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Demande de la RQ (06/10/2026) : « les cartouches, est-ce bien utilisé ? je
 * veux pouvoir les voir ». On ne pouvait les voir qu'en ouvrant l'aperçu PDF
 * d'un vrai document, et seulement dans l'état où ce document se trouvait :
 * personne ne voyait jamais côte à côte le pied d'une copie maîtrisée et celui
 * d'une copie non maîtrisée, ni l'effet d'une case décochée.
 *
 * ⚠️ C'est un APERÇU, pas la source : le cartouche opposable est fabriqué par le
 * serveur (`backend/src/cartouche-docx.ts`, alimenté par `cartoucheDe()` dans
 * `backend/src/api/ged/controllers/apercu.ts`). Les deux doivent dire la même
 * chose — en particulier la règle « en vigueur le … DIT déjà le statut », qui
 * supprime le mot « En vigueur » de la barre, et les couleurs de la mention de
 * pied. Toute modification ici se fait des deux côtés.
 *
 * Les MENTIONS, elles, ne sont pas recopiées : les deux côtés appellent le même
 * module (`lib/mentionsCartouche.ts`, miroir au caractère près de son jumeau
 * serveur, vérifié par `backend/tests/mentions-cartouche.test.js`).
 */
import React from 'react';
import type { GedSettings } from '@/lib/types';
import { mentionDe } from '@/lib/mentionsCartouche';
import { EXEMPLE, jjmmaaaa, valeursExemple } from './exempleCartouche';

/** Les hex du cartouche injecté, repris tels quels (OOXML n'a pas de jetons). */
const NAVY = 'var(--navy-900)';
const GRIS = 'var(--gray-600)';
const ROUGE = 'var(--red-600)';
const VERT = 'var(--green-600)';

const Ligne = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{ fontFamily: 'Calibri, var(--font-sans)', lineHeight: 1.35, ...style }}>{children}</div>
);

function Pied({ mention, couleur, pied, pagination }: { mention: string; couleur: string; pied: string; pagination: boolean }) {
  return (
    <div style={{ textAlign: 'center', padding: '10px 14px' }}>
      <Ligne style={{ fontSize: 11, fontWeight: 700, color: couleur }}>{mention}</Ligne>
      <Ligne style={{ fontSize: 10.5, color: GRIS }}>{pied}{pagination ? '   —   page 1 / 3' : ''}</Ligne>
    </div>
  );
}

export function ApercuCartouche({ branding, par }: { branding: GedSettings['branding']; par: string }) {
  const cart = branding.cartouche || ({} as GedSettings['branding']['cartouche']);
  const aujourdhui = jjmmaaaa(new Date());
  const pied = branding.footer || 'Document maîtrisé du SMQ.';
  const valeurs = valeursExemple(branding.labName, par);
  const mentions = branding.mentions;

  /* La barre, à l'identique de `cartoucheDe()` : « En vigueur » n'est pas écrit
     quand la date de vigueur est affichée, elle le dit déjà. */
  const datee = cart.date !== false;
  const barre = [
    cart.reference !== false ? EXEMPLE.code : '',
    cart.version !== false ? `version ${EXEMPLE.version}` : '',
    datee ? '' : 'En vigueur',
    datee ? `en vigueur le ${aujourdhui}` : '',
  ].filter(Boolean).join('   ·   ');

  const page: React.CSSProperties = {
    background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
    overflow: 'hidden', boxShadow: '0 1px 3px rgba(14,39,64,0.08)', position: 'relative',
  };

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 7 }}>
        Aperçu — ce qui s’imprime
      </div>

      <div style={page}>
        {/* EN-TÊTE */}
        <div style={{ padding: '12px 14px 9px' }}>
          <Ligne style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>
            {branding.labName || 'BIO24 Alliance'}
            {branding.mention ? <span style={{ fontSize: 11, fontWeight: 400, color: GRIS }}>{'   '}{branding.mention}</span> : null}
          </Ligne>
          <Ligne style={{ fontSize: 11, fontWeight: 700, color: NAVY, borderBottom: `1.5px solid ${NAVY}`, paddingBottom: 3, marginTop: 3, minHeight: 16 }}>
            {barre || <span style={{ color: GRIS, fontWeight: 400, fontStyle: 'italic' }}>aucune case cochée — la barre serait vide</span>}
          </Ligne>
          <Ligne style={{ fontSize: 10, color: GRIS, marginTop: 3 }}>
            Rédigé par {EXEMPLE.redacteurs} · Vérifié par {EXEMPLE.verificateur} · Approuvé par {EXEMPLE.approbateur}
          </Ligne>
        </div>

        {/* LE CORPS, figuré. Le filigrane en diagonale s'y pose comme sur le PDF —
            c'est la seule façon de juger une phrase qui barre une page entière. */}
        <div style={{ position: 'relative', height: 58, background: 'repeating-linear-gradient(to bottom, var(--gray-50) 0 7px, transparent 7px 14px)', borderTop: '1px dashed var(--border-subtle)', borderBottom: '1px dashed var(--border-subtle)', overflow: 'hidden' }}>
          <div aria-hidden="true" style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            transform: 'rotate(-12deg)', color: 'var(--red-500)', opacity: 0.42, fontWeight: 800, fontSize: 15,
            fontFamily: 'Calibri, var(--font-sans)', whiteSpace: 'nowrap',
          }}>
            {mentionDe(mentions, 'enValidation', valeurs)}
          </div>
        </div>

        {/* LES DEUX PIEDS, côte à côte : c'est la seule façon de voir que la
            couleur change avec l'exemplaire, et pas avec le réglage. */}
        <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: '1px solid var(--border-subtle)' }}>
          <div style={{ borderRight: '1px solid var(--border-subtle)' }}>
            <Pied pagination={cart.pagination !== false} pied={pied} couleur={ROUGE}
              mention={mentionDe(mentions, 'copieNonMaitrisee', valeurs)} />
          </div>
          <div>
            <Pied pagination={cart.pagination !== false} pied={pied} couleur={VERT}
              mention={mentionDe(mentions, 'copieMaitrisee', valeurs)} />
          </div>
        </div>
      </div>

      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 9, lineHeight: 1.5 }}>
        La première ligne du pied nomme l’<strong>exemplaire</strong> :
        <span style={{ color: VERT, fontWeight: 600 }}> en vert</span> quand la feuille est déclarée au registre des
        copies maîtrisées — on sait laquelle remplacer et où ;
        <span style={{ color: ROUGE, fontWeight: 600 }}> en rouge</span> sinon. La seconde vient du champ
        « Pied de page ». Son <strong>texte</strong> se règle plus bas, situation par situation ; sa
        <strong> couleur</strong>, non : elle suit l’exemplaire.
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
        Le champ « Référentiel » ne figure <strong>dans aucun en-tête de document</strong> : retiré de l’aperçu
        officiel le 03/10/2026, puis de l’impression écran le 09/10 pour que les deux disent la même chose. Il sert
        au compte-rendu de revue de direction.
      </div>
    </div>
  );
}
